import { useState, type ReactNode } from "react";
import type { HelpConfirmCard, HelpConfirmRow } from "@/types/help";
import { talkUiLng } from "@/utils/talkWelt";
import { useTalkT } from "./useTalkT";

const SPAN_REPLY = { de: "ganze Spanne", en: "whole span" } as const;

type AskCase = "whole" | "lessons" | "span" | "days";

function isWhole(row: HelpConfirmRow | undefined): boolean {
  return row?.whole === true;
}

function lessonRows(rows: HelpConfirmRow[]): HelpConfirmRow[] {
  return rows.filter((row) => !isWhole(row));
}

function isMultiDay(rows: HelpConfirmRow[]): boolean {
  const lessons = lessonRows(rows);
  if (!lessons.length || lessons.some((row) => !row.date)) return false;
  return new Set(lessons.map((row) => row.date)).size > 1;
}

function dayGroups(rows: HelpConfirmRow[]): { date: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const row of lessonRows(rows)) {
    if (!row.date) continue;
    counts.set(row.date, (counts.get(row.date) || 0) + 1);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));
}

function parseIso(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year ?? 0, (month || 1) - 1, day || 1);
}

function dayLabel(iso: string, lng: "de" | "en"): string {
  const date = parseIso(iso);
  const week = new Intl.DateTimeFormat(lng, { weekday: "short" })
    .format(date)
    .replace(/\.$/, "");
  const stamp = `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.`;
  return `${week} ${stamp}`;
}

function mailHeading(iso: string, lng: "de" | "en"): string {
  const date = parseIso(iso);
  const week = new Intl.DateTimeFormat(lng, { weekday: "short" })
    .format(date)
    .replace(/\.$/, "");
  const month = new Intl.DateTimeFormat(lng, { month: "long" }).format(date);
  return `${week} ${date.getDate()}. ${month}`;
}

function rangeLabel(dates: string[], lng: "de" | "en"): string {
  const firstIso = dates[0];
  const lastIso = dates[dates.length - 1];
  if (!firstIso || !lastIso) return "";
  const first = parseIso(firstIso);
  const last = parseIso(lastIso);
  const month = new Intl.DateTimeFormat(lng, { month: "long" }).format(last);
  if (
    first.getMonth() === last.getMonth() &&
    first.getFullYear() === last.getFullYear()
  ) {
    return `${first.getDate()}.–${last.getDate()}. ${month}`;
  }
  const startMonth = new Intl.DateTimeFormat(lng, { month: "long" }).format(
    first
  );
  return `${first.getDate()}. ${startMonth}–${last.getDate()}. ${month}`;
}

function slotLessons(rows: HelpConfirmRow[]): HelpConfirmRow[] {
  return rows.filter((row) => row.slot_id);
}

function clockFromTitle(title: string): { start: string; end: string } | null {
  const pair = title.match(/(\d{1,2}:\d{2})\s*[–-]\s*(\d{1,2}:\d{2})/);
  const pairStart = pair?.[1];
  const pairEnd = pair?.[2];
  if (pairStart && pairEnd) return { start: pairStart, end: pairEnd };
  const one = title.match(/(\d{1,2}:\d{2})/);
  const only = one?.[1];
  return only ? { start: only, end: only } : null;
}

function clockSpan(lessons: HelpConfirmRow[]): string {
  const clocks = lessons
    .map((row) => {
      if (row.start && row.end) return { start: row.start, end: row.end };
      return clockFromTitle(row.title || "");
    })
    .filter((clock): clock is { start: string; end: string } => Boolean(clock));
  const first = clocks[0];
  const last = clocks[clocks.length - 1];
  if (!first || !last) return "";
  return `${first.start}–${last.end}`;
}

const WEEK = {
  de: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
  en: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
} as const;

function weekSpan(dates: string[], lng: "de" | "en"): string {
  const names = WEEK[lng];
  const firstIso = dates[0];
  const lastIso = dates[dates.length - 1];
  if (!firstIso || !lastIso) return "";
  const first = names[parseIso(firstIso).getDay()] ?? "";
  const last = names[parseIso(lastIso).getDay()] ?? "";
  return first === last ? first : `${first}–${last}`;
}

function AskFrame({
  busy,
  eyebrow,
  ask,
  back,
  onBack,
  onDiscard,
  abortLabel,
  sendLabel,
  reply,
  onSubmit,
  children,
}: {
  busy: boolean;
  eyebrow?: string;
  ask?: string;
  back?: string;
  onBack?: () => void;
  onDiscard: () => void;
  abortLabel: string;
  sendLabel: string;
  reply?: string | null;
  onSubmit?: (text: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="talk-ask-veil">
      <div className="talk-ask-card">
        {back ? (
          <button
            type="button"
            className="talk-ask-back"
            disabled={busy}
            onClick={onBack}
          >
            {back}
          </button>
        ) : null}
        {eyebrow ? <div className="talk-ask-eyebrow">{eyebrow}</div> : null}
        {ask ? <p className="talk-ask-line">{ask}</p> : null}
        {children}
        {onSubmit ? (
          <div className="talk-ask-actions">
            <button
              type="button"
              className="talk-ask-go"
              disabled={busy || !reply}
              onClick={() => {
                if (reply) onSubmit(reply);
              }}
            >
              {sendLabel}
            </button>
            <button type="button" disabled={busy} onClick={onDiscard}>
              {abortLabel}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TalkConfirmCard({
  card,
  light = false,
  spoken = false,
  busy = false,
  onSend,
  onDiscard,
  onPick,
}: {
  card: HelpConfirmCard;
  light?: boolean;
  spoken?: boolean;
  busy?: boolean;
  onSend: () => void;
  onDiscard: () => void;
  onPick?: (text: string) => void;
}) {
  const t = useTalkT();
  const rows = card.rows || [];
  const [door, setDoor] = useState<"pick" | "open">("pick");
  const [askCase, setAskCase] = useState<AskCase | null>(null);
  const [lessonIds, setLessonIds] = useState<string[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const lng = talkUiLng();
  const backLabel = t("talk_ask_other_door");
  const sendLabel = t("talk_ask_submit");
  const abortLabel = t("talk_ask_abort");
  const goBack = () => {
    setLessonIds([]);
    setDays([]);
    setAskCase(null);
    setDoor("pick");
  };
  const openCase = (next: AskCase) => {
    setLessonIds([]);
    setDays([]);
    setAskCase(next);
    setDoor("open");
  };
  const submit = (text: string) => onPick?.(text);
  if (card.ask && isMultiDay(rows)) {
    const groups = dayGroups(rows);
    const dates = groups.map((group) => group.date);
    const range = rangeLabel(dates, lng);
    const frame = {
      busy,
      onDiscard,
      abortLabel,
      sendLabel,
      back: door === "pick" ? undefined : backLabel,
      onBack: goBack,
    };
    if (door === "pick") {
      return (
        <AskFrame
          {...frame}
          eyebrow={card.status_label || t("talk_followup")}
          ask={`${range}. ${t("talk_ask_what")}`}
        >
          <div className="talk-ask-doors">
            <button
              type="button"
              className="talk-ask-door"
              disabled={busy}
              onClick={() => openCase("span")}
            >
              <strong>{t("talk_ask_span")}</strong>
              <span>{t("talk_ask_span_hint")}</span>
            </button>
            <button
              type="button"
              className="talk-ask-door"
              disabled={busy}
              onClick={() => openCase("days")}
            >
              <strong>{t("talk_ask_days")}</strong>
              <span>{t("talk_ask_days_hint")}</span>
            </button>
          </div>
        </AskFrame>
      );
    }
    if (askCase === "span") {
      return (
        <AskFrame
          {...frame}
          eyebrow={t("talk_ask_span")}
          reply={SPAN_REPLY[lng]}
          onSubmit={submit}
        >
          <div className="talk-ask-plate">
            <strong>{weekSpan(dates, lng)}</strong>
            <span>
              {t("talk_ask_span_body", { range, count: dates.length })}
            </span>
          </div>
        </AskFrame>
      );
    }
    const dateReply = days.length ? [...days].sort().join(" ") : null;
    return (
      <AskFrame
        {...frame}
        eyebrow={t("talk_ask_days_step")}
        ask={t("talk_ask_days_pick")}
        reply={dateReply}
        onSubmit={submit}
      >
        <div className="talk-ask-days">
          {groups.map((group) => {
            const on = days.includes(group.date);
            return (
              <button
                type="button"
                className={`talk-ask-door talk-ask-day${on ? " is-selected" : ""}`}
                key={group.date}
                disabled={busy}
                aria-pressed={on}
                onClick={() =>
                  setDays((prev) =>
                    prev.includes(group.date)
                      ? prev.filter((date) => date !== group.date)
                      : [...prev, group.date]
                  )
                }
              >
                <strong>{dayLabel(group.date, lng)}</strong>
                <span>{t("talk_ask_lessons", { count: group.count })}</span>
              </button>
            );
          })}
        </div>
      </AskFrame>
    );
  }
  if (card.ask) {
    const whole = rows.find((row) => isWhole(row));
    const lessons = slotLessons(rows).length
      ? slotLessons(rows)
      : lessonRows(rows);
    if (lessons.length > 0 || whole) {
      const span = clockSpan(lessons);
      const count = lessons.length;
      const dated = lessons.find((row) => row.date)?.date;
      const dayName = dated
        ? new Intl.DateTimeFormat(lng, {
            weekday: "long",
            day: "numeric",
            month: "long",
          }).format(parseIso(dated))
        : "";
      const frame = {
        busy,
        onDiscard,
        abortLabel,
        sendLabel,
        back: door === "pick" ? undefined : backLabel,
        onBack: goBack,
      };
      if (door === "pick") {
        return (
          <AskFrame
            {...frame}
            eyebrow={card.status_label || t("talk_followup")}
            ask={
              dayName ? `${dayName}. ${t("talk_ask_what")}` : t("talk_ask_what")
            }
          >
            <div className="talk-ask-doors">
              <button
                type="button"
                className="talk-ask-door"
                disabled={busy}
                onClick={() => openCase("whole")}
              >
                <strong>{t("talk_ask_whole_door")}</strong>
                <span>{t("talk_ask_all", { span, count })}</span>
              </button>
              <button
                type="button"
                className="talk-ask-door"
                disabled={busy}
                onClick={() => openCase("lessons")}
              >
                <strong>{t("talk_ask_lesson_door")}</strong>
                <span>{t("talk_ask_lessons_hint")}</span>
              </button>
            </div>
          </AskFrame>
        );
      }
      if (askCase === "whole") {
        const reply = whole?.send || whole?.title || SPAN_REPLY[lng];
        const when = [dayName, span].filter(Boolean).join(", ");
        return (
          <AskFrame
            {...frame}
            eyebrow={t("talk_ask_whole_door")}
            ask={t("talk_ask_whole_line", {
              when: when ? `${when}, ` : "",
              count,
            })}
            reply={reply}
            onSubmit={submit}
          />
        );
      }
      const lessonKey = (row: HelpConfirmRow) =>
        row.slot_id || row.send || row.title || "";
      const reply = lessonIds.length ? `ids:${lessonIds.join(" ")}` : null;
      const toggleLesson = (id: string) => {
        setLessonIds((prev) =>
          prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
      };
      return (
        <AskFrame
          {...frame}
          eyebrow={t("talk_ask_lessons_step")}
          ask={t("talk_ask_pick")}
          reply={reply}
          onSubmit={submit}
        >
          <div className="talk-ask-lessons">
            {lessons.map((row) => {
              const id = lessonKey(row);
              return (
                <button
                  type="button"
                  className={`talk-ask-row${lessonIds.includes(id) ? " is-selected" : ""}`}
                  key={id}
                  disabled={busy}
                  aria-pressed={lessonIds.includes(id)}
                  onClick={() => {
                    if (id) toggleLesson(id);
                  }}
                >
                  <b>{row.title}</b>
                  <span>{[row.sub, row.label].filter(Boolean).join(" ")}</span>
                </button>
              );
            })}
          </div>
        </AskFrame>
      );
    }
  }
  const mailRows = rows.filter(
    (row) => row.date && (row.start || row.subject || row.klass)
  );
  if (!card.ask && mailRows.length) {
    const byDay = new Map<string, HelpConfirmRow[]>();
    for (const row of mailRows) {
      const list = byDay.get(row.date || "") || [];
      list.push(row);
      byDay.set(row.date || "", list);
    }
    const dates = [...byDay.keys()].sort();
    const onlyDate = dates[0];
    const when =
      dates.length === 1 && onlyDate
        ? mailHeading(onlyDate, lng)
        : rangeLabel(dates, lng);
    return (
      <AskFrame
        busy={busy}
        eyebrow={card.status_label || t("talk_confirm_status")}
        ask={t("talk_confirm_out", { when, count: mailRows.length })}
        onDiscard={onDiscard}
        abortLabel={t("talk_discard")}
        sendLabel={t("talk_send")}
        reply="send"
        onSubmit={() => onSend()}
      >
        {dates.map((iso) => (
          <div key={iso}>
            <div className="talk-mail-day">{mailHeading(iso, lng)}</div>
            <table className="talk-mail">
              <thead>
                <tr>
                  <th>{t("talk_col_time")}</th>
                  <th>{t("talk_col_subject")}</th>
                  <th>{t("talk_col_class")}</th>
                </tr>
              </thead>
              <tbody>
                {[...byDay.get(iso)!]
                  .sort((a, b) => (a.start || "").localeCompare(b.start || ""))
                  .map((row, i) => (
                    <tr key={`${iso}-${row.start}-${i}`}>
                      <td>
                        {row.start && row.end
                          ? `${row.start}–${row.end}`
                          : row.title}
                      </td>
                      <td>{row.subject || row.sub}</td>
                      <td>{row.klass || row.label}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))}
      </AskFrame>
    );
  }
  return (
    <div
      className={`talk-confirm-veil ${card.low ? "low" : ""} ${light ? "light" : ""}`}
    >
      <div className={`talk-write-card ${light ? "light" : ""}`}>
        <div className="talk-confirm-eyebrow">
          <span>{card.status_label || t("talk_confirm_status")}</span>
          {spoken && !light ? (
            <span className="talk-confirm-speak">
              {t("talk_confirm_spoken")}
            </span>
          ) : null}
        </div>
        <p className="talk-confirm-pending">{card.pending_line}</p>
        {rows.length > 1 ? (
          <div className="talk-confirm-rows">
            {rows.map((row, i) => (
              <div className="talk-confirm-row" key={`${row.label}-${i}`}>
                <b>{row.label}</b>
                <span>{[row.title, row.sub].filter(Boolean).join(" · ")}</span>
              </div>
            ))}
          </div>
        ) : (
          <>
            {rows[0]?.title ? <h4>{rows[0].title}</h4> : null}
            {rows[0]?.sub ? <p className="sub">{rows[0].sub}</p> : null}
            {rows[0]?.act ? <p className="act">{rows[0].act}</p> : null}
          </>
        )}
        {card.hint ? <p className="hint">{card.hint}</p> : null}
        <div className="talk-confirm-actions">
          <button
            type="button"
            className="talk-orb-action send"
            disabled={busy}
            onClick={onSend}
          >
            {t("talk_send")}
          </button>
          <button
            type="button"
            className="talk-orb-action discard"
            disabled={busy}
            onClick={onDiscard}
          >
            {t("talk_discard")}
          </button>
        </div>
      </div>
    </div>
  );
}
